import type {
  AssignableOverride,
  Channel,
  ComplianceStatus,
  LinkKind,
  MyProjectStatus,
  ProjectStatus,
  ReportStatus,
  RequestKind,
  RequestStatus,
  SyncStatus,
} from "../api/types";
import { t, translated } from "./i18n";

/** The six tones shared by the DS Tag, Status and Alert components. */
export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "critical";

export interface Labelled {
  label: string;
  tone: Tone;
}

/** The label is read in the current language each time (keys are the English copy). */
function labelled(label: string, tone: Tone): Labelled {
  return {
    get label() {
      return t(label);
    },
    tone,
  };
}

export const PROJECT_STATUS: Record<ProjectStatus, Labelled> = {
  active: labelled("Active", "success"),
  paused: labelled("Paused", "warning"),
  closed: labelled("Closed", "neutral"),
};

export const MY_STATUS: Record<Exclude<MyProjectStatus, "none">, Labelled> = {
  member: labelled("Member", "success"),
  pending: labelled("Request pending", "warning"),
};

export const REQUEST_KIND: Record<RequestKind, string> = translated({
  join: "Join",
  leave: "Leave",
});

export const REQUEST_STATUS: Record<RequestStatus, Labelled> = {
  pending: labelled("Pending", "warning"),
  accepted: labelled("Accepted", "success"),
  refused: labelled("Refused", "danger"),
  cancelled: labelled("Cancelled", "neutral"),
};

export const REPORT_STATUS: Record<ReportStatus, Labelled> = {
  draft: labelled("Draft", "warning"),
  submitted: labelled("Submitted", "success"),
};

export const COMPLIANCE_STATUS: Record<ComplianceStatus, Labelled> = {
  missing: labelled("Missing", "danger"),
  draft: labelled("Draft", "warning"),
  submitted: labelled("Submitted", "success"),
  absent: labelled("Absent", "info"),
};

export const OVERRIDE: Record<AssignableOverride, string> = translated({
  none: "Automatic (rule)",
  in: "Forced in",
  out: "Forced out",
});

export const SYNC_STATUS: Record<SyncStatus, Labelled> = {
  running: labelled("Running", "info"),
  success: labelled("Succeeded", "success"),
  failed: labelled("Failed", "danger"),
};

export const SYNC_TRIGGER: Record<string, string> = translated({
  schedule: "Scheduled",
  manual: "Manual",
});

export const LINK_KIND: Record<LinkKind, string> = translated({
  github: "GitHub",
  gitlab: "GitLab",
  "azure-devops": "Azure DevOps",
  bitbucket: "Bitbucket",
  git: "Git",
  sharepoint: "SharePoint",
  teams: "Teams",
  atlassian: "Confluence / Jira",
  figma: "Figma",
  notion: "Notion",
  website: "Website",
});

export const CHANNEL: Record<Channel, string> = translated({
  ui: "Interface",
  mcp: "Claude (MCP)",
  job: "Scheduled job",
});

/** Audit actions (`audit.record(...)` calls in the backend services). */
export const HISTORY_ACTION: Record<string, string> = translated({
  "project.create": "Project created",
  "project.update": "Project updated",
  "project.status": "Status changed",
  "project.roles": "TL/PO appointed",
  "project.repositories": "Links changed",
  "stream.create": "Stream added",
  "stream.update": "Stream updated",
  "stream.archive": "Stream archived",
  "assignment.create": "Assignment",
  "assignment.end": "Assignment ended",
  "request.join": "Request to join",
  "request.leave": "Request to leave",
  "request.cancel": "Request cancelled",
  "request.decide": "DAR decision",
});

export function historyActionLabel(action: string): string {
  return HISTORY_ACTION[action] ?? action;
}
